import {
	InvoiceBatchCustomFieldConstraint,
	InvoiceBatchFilter,
	InvoiceBatchListItem,
	InvoiceBatchScopeCustomerRow,
} from "../../../models/InvoiceBatch";
import { CustomFieldDefinition } from "../../../models/Organisation";

/**
 * One-line summary of what a batch was scoped to, for the batch list.
 *
 * Only counts per group — resolving ids to names would mean loading the tag,
 * subsidiary and contract taxonomies just to label a list column. Batches
 * created before customer filtering fall back to the deprecated single `pool`.
 */
export function describeInvoiceBatchScope(
	invoiceBatch: Pick<InvoiceBatchListItem, "filter" | "pool">,
	t: (key: string) => string
) {
	const { filter, pool } = invoiceBatch;

	if (!filter) return pool || "-";

	const parts = [
		filter.pools?.length && `${t("POOL")}: ${filter.pools.length}`,
		filter.subsidiaryIds?.length && `${t("SUBSIDIARY")}: ${filter.subsidiaryIds.length}`,
		filter.tagKeys?.length && `${t("invoiceBatch.filter.customerTags")}: ${filter.tagKeys.length}`,
		filter.contractIds?.length && `${t("CONTRACT")}: ${filter.contractIds.length}`,
		filter.customFields?.length &&
			`${t("invoiceBatch.filter.customFields")}: ${filter.customFields.length}`,
		filter.excludedCustomerIds?.length &&
			`${t("invoiceBatch.filter.excludedCustomers")}: ${filter.excludedCustomerIds.length}`,
	].filter(Boolean);

	return parts.length ? parts.join(" · ") : pool || "-";
}

/** The constraint currently set for a custom field key, if any. */
export function getCustomFieldConstraint(
	filter: InvoiceBatchFilter,
	key: string
): InvoiceBatchCustomFieldConstraint | undefined {
	return filter.customFields?.find((constraint) => constraint.key === key);
}

/**
 * Sets (or with `undefined`, clears) the constraint for one custom field.
 * Constraints are keyed uniquely; the array is dropped from the filter when
 * it empties so an unconstrained filter still serialises as `{}`.
 */
export function setCustomFieldConstraint(
	filter: InvoiceBatchFilter,
	key: string,
	value: InvoiceBatchCustomFieldConstraint["value"] | undefined
): InvoiceBatchFilter {
	const others = (filter.customFields ?? []).filter((constraint) => constraint.key !== key);
	const next = value === undefined ? others : [...others, { key, value }];

	const updated: InvoiceBatchFilter = { ...filter, customFields: next };
	if (!next.length) delete updated.customFields;

	return updated;
}

/**
 * Translates a raw input value into a constraint value for a definition.
 * Empty text / NaN means "no constraint" (`undefined`); booleans are passed
 * through; numbers are parsed from the input's text.
 */
export function parseCustomFieldConstraintValue(
	definition: Pick<CustomFieldDefinition, "type">,
	raw: string | boolean | undefined
): InvoiceBatchCustomFieldConstraint["value"] | undefined {
	if (raw === undefined) return undefined;

	switch (definition.type) {
		case "boolean":
			return typeof raw === "boolean" ? raw : undefined;
		case "number": {
			if (typeof raw !== "string" || raw.trim() === "") return undefined;
			const num = Number(raw);
			return Number.isFinite(num) ? num : undefined;
		}
		default: {
			if (typeof raw !== "string") return undefined;
			const text = raw.trim();
			return text ? text : undefined;
		}
	}
}

/** Number of custom-field constraints set — the badge on the "Avancerat" section. */
export function countCustomFieldConstraints(filter: InvoiceBatchFilter): number {
	return filter.customFields?.length ?? 0;
}

/**
 * Human-readable "Label: value" chips for the constraints, resolved against
 * the org's definitions. A constraint whose definition has since been
 * archived/removed still shows, keyed by its raw key, so an active constraint
 * is never invisible.
 */
export function describeCustomFieldConstraints(
	filter: InvoiceBatchFilter,
	definitions: Pick<CustomFieldDefinition, "key" | "label" | "options">[],
	t: (key: string) => string
): { key: string; label: string }[] {
	const labelByKey = new Map(
		definitions.map((definition) => [definition.key, definition.label.sv])
	);
	const optionsByKey = new Map(
		definitions.map((definition) => [definition.key, definition.options])
	);

	return (filter.customFields ?? []).map((constraint) => {
		const label = labelByKey.get(constraint.key) ?? constraint.key;
		const optionLabel = optionsByKey
			.get(constraint.key)
			?.find((option) => option.key === constraint.value)?.label.sv;
		const value =
			typeof constraint.value === "boolean"
				? t(constraint.value ? "YES" : "NO")
				: optionLabel ?? String(constraint.value);
		return { key: constraint.key, label: `${label}: ${value}` };
	});
}

/**
 * Excludes or re-includes several customers at once — what the (de)select-all
 * action applies to the rows currently visible in the list.
 *
 * Only the given ids are touched: customers hidden by an active search keep
 * whatever state they had, so "select all" on a search result cannot silently
 * re-include the rest of the org. The array is dropped when it empties, so an
 * unconstrained filter still serialises as `{}`.
 */
export function setCustomersExcluded(
	filter: InvoiceBatchFilter,
	ids: string[],
	excluded: boolean
): InvoiceBatchFilter {
	const current = new Set(filter.excludedCustomerIds ?? []);

	for (const id of ids) {
		if (excluded) current.add(id);
		else current.delete(id);
	}

	const next = [...current];
	const updated: InvoiceBatchFilter = { ...filter, excludedCustomerIds: next };
	if (!next.length) delete updated.excludedCustomerIds;

	return updated;
}

/**
 * Drops manual exclusions that no longer reference a customer in the resolved
 * scope, so a persisted batch never carries inert ids. Exclusions are kept in
 * form state untouched while filters change (toggling a filter back restores
 * the manual work) — pruning happens only at submit. With no rows to check
 * against (the projection has not resolved) the filter is returned untouched.
 */
export function pruneExcludedCustomerIds(
	filter: InvoiceBatchFilter,
	rows?: Pick<InvoiceBatchScopeCustomerRow, "id">[]
): InvoiceBatchFilter {
	if (!filter.excludedCustomerIds?.length || !rows) return filter;

	const present = new Set(rows.map((row) => row.id));
	const next = filter.excludedCustomerIds.filter((id) => present.has(id));

	const updated: InvoiceBatchFilter = { ...filter, excludedCustomerIds: next };
	if (!next.length) delete updated.excludedCustomerIds;

	return updated;
}

/**
 * Number of constraints in the groups the advanced dialog owns — the badge on
 * the "Avancerat urval" trigger. Pools and subsidiaries are not counted; they
 * live on the form.
 */
export function countAdvancedFilterConstraints(filter: InvoiceBatchFilter): number {
	return (
		(filter.tagKeys?.length ?? 0) +
		(filter.contractIds?.length ?? 0) +
		(filter.customFields?.length ?? 0) +
		(filter.excludedCustomerIds?.length ?? 0)
	);
}
